import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Coins, ReceiptText, ShieldAlert } from 'lucide-react';
import api from '../../../utils/api';
import { PartnerEndpoints } from '../../../utils/endpoints';
import { formatCount, formatNaira } from '../../../utils/money';
import { formatLagos } from '../../../utils/datetime';
import { Constants } from '../../../utils/constants';
import PageHeader from '../../shell/PageHeader';
import { Alert, Button, Card, EmptyState, Skeleton, StatusPill } from '../../ui';

const [ACTIVE, INACTIVE] = Constants.agentActiveStatuses;

const breadcrumbs = [{ label: 'Home', to: '/layout' }, { label: 'Agent Dashboard' }];

const MetricTile = ({ label, value, loading }) => (
  <Card className="mb-0">
    <p className="mb-2 text-base text-theme-secondarytextcolor dark:text-themedark-secondarytextcolor">{label}</p>
    {loading ? (
      <Skeleton w="70%" h="1.75rem" />
    ) : (
      <p className="text-f-h4 font-semibold text-theme-headings dark:text-themedark-headings">{value}</p>
    )}
  </Card>
);

const ProfileField = ({ label, value, loading }) => (
  <div className="flex items-center justify-between gap-4 border-b border-theme-border py-2 dark:border-themedark-border">
    <span className="text-base text-theme-secondarytextcolor dark:text-themedark-secondarytextcolor">{label}</span>
    {loading ? (
      <Skeleton w="100px" h="1rem" />
    ) : (
      <span className="font-medium text-theme-headings dark:text-themedark-headings">{value}</span>
    )}
  </div>
);

const QuickLink = ({ to, icon, title, description }) => (
  <Link to={to} className="block">
    <Card className="mb-0 h-full transition-shadow hover:shadow-lg">
      <div className="flex items-center gap-4">
        <span className="text-primary-500">{icon}</span>
        <div>
          <h6 className="text-f-h6 font-semibold text-theme-headings dark:text-themedark-headings">{title}</h6>
          <p className="text-base text-theme-secondarytextcolor dark:text-themedark-secondarytextcolor">
            {description}
          </p>
        </div>
        <ArrowRight className="ml-auto h-4 w-4 shrink-0 text-theme-secondarytextcolor" aria-hidden="true" />
      </div>
    </Card>
  </Link>
);

const AgentDashboard = () => {
  const [profile, setProfile] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notAnAgent, setNotAnAgent] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setNotAnAgent(false);
    try {
      const [profileResponse, summaryResponse] = await Promise.all([
        api.get(PartnerEndpoints.agentProfile),
        api.get(PartnerEndpoints.agentCommissionsSummary),
      ]);
      setProfile(profileResponse.data?.data ?? null);
      setSummary(summaryResponse.data?.data ?? null);
    } catch (err) {
      if (err.response?.status === 403) setNotAnAgent(true);
      else setError('Could not load your agent data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (notAnAgent) {
    return (
      <>
        <PageHeader title="Agent Dashboard" breadcrumbs={breadcrumbs} />
        <Card>
          <EmptyState
            icon={<ShieldAlert className="h-10 w-10" aria-hidden="true" />}
            title="Not a CashTap agent"
            message="Your merchant account is not registered as a CashTap agent. Contact support if you believe this is an error."
            action={
              <Link to="/layout" className="btn btn-outline-primary">
                Go to dashboard
              </Link>
            }
          />
        </Card>
      </>
    );
  }

  if (error) {
    return (
      <>
        <PageHeader title="Agent Dashboard" breadcrumbs={breadcrumbs} />
        <Card>
          <Alert variant="danger">{error}</Alert>
          <Button className="mt-4" onClick={load}>
            Try again
          </Button>
        </Card>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title="Agent Dashboard"
        breadcrumbs={breadcrumbs}
        actions={!loading && <StatusPill status={profile?.is_active ? ACTIVE : INACTIVE} />}
      />

      <div className="mb-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-5">
        <MetricTile label="Total Earned" value={formatNaira(summary?.total_earned)} loading={loading} />
        <MetricTile label="Pending" value={formatNaira(summary?.total_pending)} loading={loading} />
        <MetricTile label="Paid" value={formatNaira(summary?.total_paid)} loading={loading} />
        <MetricTile label="Failed" value={formatNaira(summary?.total_failed)} loading={loading} />
        <MetricTile label="Transactions" value={formatCount(summary?.transaction_count)} loading={loading} />
      </div>

      <Card title="Agent information">
        <div className="grid gap-x-8 md:grid-cols-2">
          <ProfileField label="Business name" value={profile?.business_name || '—'} loading={loading} />
          <ProfileField label="Status" value={profile?.is_active ? ACTIVE : INACTIVE} loading={loading} />
          <ProfileField label="Commission count" value={formatCount(profile?.commission_count)} loading={loading} />
          <ProfileField
            label="Member since"
            value={formatLagos(profile?.created_at, { withTime: false })}
            loading={loading}
          />
        </div>
      </Card>

      <div className="grid gap-6 md:grid-cols-2">
        <QuickLink
          to="/layout/agent-transactions"
          icon={<ReceiptText className="h-6 w-6" aria-hidden="true" />}
          title="View transactions"
          description="See all your CashTap transactions"
        />
        <QuickLink
          to="/layout/agent-commissions"
          icon={<Coins className="h-6 w-6" aria-hidden="true" />}
          title="View commissions"
          description="Track your earned commissions"
        />
      </div>
    </>
  );
};

export default AgentDashboard;
